import { resolveSiteIconSize, type SiteIconSizeToken } from './iconSizes';
import {
  resolveSiteSocialIcon,
  type SimpleIcon,
  type SiteSocialNetwork,
} from './socialNetworks';

export interface SocialIconProps {
  /** Curated network key or any Simple Icons entry. */
  icon: SiteSocialNetwork | SimpleIcon;
  size?: number | SiteIconSizeToken;
  className?: string;
  /**
   * Decorative by default (`aria-hidden`). Set `aria-hidden={false}` (and prefer a
   * labelled parent link) when the glyph alone must be announced.
   */
  'aria-hidden'?: boolean;
  /** Optional accessible name when `aria-hidden` is false. Defaults to brand title. */
  title?: string;
}

/** Brand/social marks for site templates — Simple Icons (CC0). Not for general UI. */
export function SocialIcon({
  icon,
  size = 'md',
  className,
  'aria-hidden': ariaHidden = true,
  title,
}: SocialIconProps) {
  const brand = resolveSiteSocialIcon(icon);
  const px = resolveSiteIconSize(size);
  const label = title ?? brand.title;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={px}
      height={px}
      fill="currentColor"
      className={['ws-social-icon', className].filter(Boolean).join(' ')}
      role={ariaHidden ? undefined : 'img'}
      aria-hidden={ariaHidden ? true : undefined}
      aria-label={ariaHidden ? undefined : label}
    >
      {!ariaHidden ? <title>{label}</title> : null}
      <path d={brand.path} />
    </svg>
  );
}
